import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LinkAsButton } from "@/components/LinkAsButton";
import { EntityListPage } from "@/components/entity-page/EntityListPage";
import { getFormOptions } from "@/lib/api";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/settings/agentTypes/")({
  component: AgentTypesIndexPage,
});

function AgentTypesIndexPage() {
  return <AgentTypesIndexPageComponent />;
}

// Exported for testing
export function AgentTypesIndexPageComponent() {
  const [agentTypes, setAgentTypes] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getFormOptions("agentTypes")
      .then((options) => {
        if (!active) return;
        setAgentTypes(options);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching agent types:", err);
        setError("Failed to load agent types.");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <EntityListPage
      title="Agent Types"
      description="The categories of agents (e.g. chat vs. voice) available when creating an agent - each option here populates an agent's typeId field."
      items={agentTypes}
      loading={loading}
      error={error}
      getItemKey={(agentType) => agentType.id}
      gridClassName="grid grid-cols-1 gap-4"
      renderItem={(agentType) => (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle>{agentType.label}</CardTitle>
                <CardDescription>
                  {agentType.description || `Value: ${agentType.value}`}
                </CardDescription>
                <p className="text-xs text-muted-foreground mt-1">Order: {agentType.order ?? 0}</p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard/settings/agentTypes/$id" params={{ id: agentType.id }}>
                  Edit
                </Link>
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}
      createHref="/dashboard/settings/agentTypes/new"
      createLabel="Create Agent Type"
      emptyMessage="No agent types yet. Create one to get started."
      headerActions={
        <LinkAsButton to="/dashboard/settings" variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </LinkAsButton>
      }
    />
  );
}
